import { createContext, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { usePage } from '@inertiajs/react'
import { fetchSourceBadges } from '@/api/sourceBadges'
import type { SourceBadgeConfig, SourceBadgeMap } from '@/types/sourceBadge'
import type { SharedProps } from '@/types'

/**
 * Badge configuration, fetched once per page load and read by every surface
 * that marks a sense.
 *
 * The default is an empty map rather than a thrown error: badges are display
 * metadata, so a tree without the provider — SSR, a guest on a public page, a
 * component under test — should render the words plainly, not crash.
 */
export const SourceBadgesContext = createContext<SourceBadgeMap>({})

const bySource = (badges: SourceBadgeConfig[]): SourceBadgeMap =>
  Object.fromEntries(badges.map((badge) => [badge.source, badge]))

export function SourceBadgesProvider({ children }: { children: ReactNode }) {
  const userId = usePage<SharedProps>().props.auth?.user?.id ?? null
  const [badges, setBadges] = useState<SourceBadgeMap>({})

  useEffect(() => {
    if (userId === null) {
      setBadges({})
      return
    }
    fetchSourceBadges()
      .then((res) => setBadges(bySource(res.data.data)))
      .catch(() => {})
  }, [userId])

  return <SourceBadgesContext.Provider value={badges}>{children}</SourceBadgesContext.Provider>
}

/** The configured badges, keyed by `word_senses.source`. */
export function useSourceBadges(): SourceBadgeMap {
  return useContext(SourceBadgesContext)
}
